// ABOUTME: Shared email template editor: editable rendered view (designMode iframe) + raw html source view.
// ABOUTME: Parameterized per template type (channels, shipment preview, subject) and stores the full email html document.

import { Box, ButtonBase, Chip, IconButton, InputBase, useTheme } from "@mui/material";
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SaveIcon from '@mui/icons-material/Save';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from '@mui/icons-material/Add';
import ButtonGroup from "../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../components/theme/buttons/Shbutton";
import TextInput from "../../../components/theme/inputs/TextInput";
import Subheading from "../../../components/theme/text/Subheading";
import Text from "../../../components/theme/text/Text";
import { InputDropdown } from "../../../components/theme/dropdowns/InputDropdown";
import CountryPoolSelector from "../CountryPoolSelector";
import { countriesFromLocale, emailTemplateLanguages } from "../../../utils/countries";
import MConfirmDelete from "../../../components/global/Modals/MConfirmDelete";
import MConfirmation from "../../../components/global/Modals/MConfirmation";
import { selectUserPermission } from "../../../newapi/user/authentication/authSlice";
import { tokens } from "../../../theme";
import { showToastMessage, showToastMessagePostRequest, showToastMessageUpdateRequest, showToastMessageDeleteRequest, showToastMessageRequestError } from "../../../utils/toasts";
import {
    useGetEmailTemplateTypesQuery,
    useGetEmailTemplatesQuery,
    useGetEmailTemplateSalesChannelsQuery,
    useGetEmailTemplateShipmentsQuery,
    useGetEmailTemplateShipmentPreviewQuery,
    useAddEmailTemplateMutation,
    useUpdateEmailTemplateMutation,
    useDeleteEmailTemplateMutation,
} from "../../../newapi/emailtemplate/emailTemplateSlice";

// Shell for legacy templates whose bodyHtml is a fragment. Keep in sync with wrapHtml
// in warehouse-api/app/core/emailtemplate/trackandtrace.js — the shell the mailer puts
// around such fragments when sending.
const wrapShellHtml = (subject, innerHtml) => `<!DOCTYPE html><html lang="nl"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>${subject}</title></head><body style="margin:0;padding:0;font-family:Arial,sans-serif;background-color:#f4f4f4;color:#31415f;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;max-width:600px;margin:0 auto;background-color:#ffffff;"><tr><td style="text-align:left;padding:40px 48px;font-size:16px;line-height:150%;color:#31415f;">${innerHtml}<p style="margin:0;padding-top:24px;color:#31415f;"><i>Dit is een automatisch gegenereerd bericht.</i></p></td></tr><tr><td style="background-color:#ecf0f1;border-top:1px solid #ced7e1;padding:24px;text-align:center;line-height:150%;font-size:14px;color:#8993A4;"><p style="margin:0;">Is dit bericht niet aan jou gericht? Neem dan contact op met onze supportafdeling.</p><p style="margin:0;">&copy; 2026 Stockhub. Alle rechten voorbehouden.</p></td></tr></table></body></html>`;

const isFullHtmlDocument = (html) => /^\s*(<!doctype|<html)/i.test(String(html ?? ''));

// Device presets for the editor frame; null width means fill the available space.
const FRAME_DEVICES = [
    { key: "desktop", width: null, icon: <DesktopWindowsOutlinedIcon sx={{ fontSize: 18 }} /> },
    { key: "tablet", width: 768, icon: <TabletMacOutlinedIcon sx={{ fontSize: 18 }} /> },
    { key: "mobile", width: 375, icon: <PhoneIphoneOutlinedIcon sx={{ fontSize: 18 }} /> },
];

// Email-safe fonts and sizes offered by the selection balloon.
const BALLOON_FONTS = ["Arial", "Helvetica", "Georgia", "Times New Roman", "Verdana", "Courier New"];
const BALLOON_FONT_SIZES = [12, 13, 14, 16, 18, 21, 24, 28];

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        props.hasManagePermission &&
        <ButtonGroup version={2}>
            <Shbutton
                isLoading={props.isLoading}
                className={"group-btn"}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
        </ButtonGroup>
    )
};

const EmailTemplateEditor = (props) => {
    const {
        type,
        titleKey,
        managePrivileges,
        enableChannels = false,
        enableShipmentPreview = false,
        showSubject = true,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const hasManagePermission = useSelector((state) =>
        selectUserPermission(state, managePrivileges)
    );

    const { data: templateTypes } = useGetEmailTemplateTypesQuery();
    const { data: salesChannels } = useGetEmailTemplateSalesChannelsQuery(undefined, { skip: !enableChannels });
    // Search runs server-side (there can be hundreds of thousands of shipments);
    // the backend caps results at 10.
    const [shipmentSearch, setShipmentSearch] = useState("");
    const { data: exampleShipments } = useGetEmailTemplateShipmentsQuery(shipmentSearch, { skip: !enableShipmentPreview });

    // Optional example shipment: its resolved shortcode values are shown per shortcode.
    const [exampleShipmentId, setExampleShipmentId] = useState(null);
    const { data: shipmentPreview } = useGetEmailTemplateShipmentPreviewQuery(exampleShipmentId, { skip: !exampleShipmentId });

    // null = the scope's default template; otherwise a saleschannel id (channels only).
    const [selectedChannelId, setSelectedChannelId] = useState(null);

    // Every stored template of the selected scope (one per set of countries) and, for a
    // saleschannel, the relation default scope it falls back on. currentData (not data)
    // so a deleted template never leaves stale content behind on refetch.
    const { currentData: scopeTemplates, error: scopeTemplatesError, isFetching: scopeTemplatesFetching } =
        useGetEmailTemplatesQuery({ type, webshopCredentialsId: selectedChannelId });
    const { currentData: defaultScopeTemplates, error: defaultScopeError, isFetching: defaultScopeFetching } =
        useGetEmailTemplatesQuery({ type, webshopCredentialsId: null });
    const scopeRows = scopeTemplates ?? [];
    const isForAllCountries = (row) => (row?.countries ?? []).length === 0;
    // The relation default: the all-countries template of the default scope, which a
    // channel without its own template falls back on.
    const defaultTemplate = (defaultScopeTemplates ?? []).find(isForAllCountries) ?? null;

    // Which template of the scope is open: null = not chosen yet (the all-countries
    // template, else the first stored one, else a new template), a number = a stored
    // template, "new" = a template that is not saved yet. A mail to a country uses the
    // template listing that country, else the template without countries, else the
    // Dutch one (the backend resolver).
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const autoTemplate = scopeRows.find(isForAllCountries) ?? scopeRows[0] ?? null;
    const selectedTemplate = typeof selectedTemplateId === "number"
        ? (scopeRows.find((row) => row.id === selectedTemplateId) ?? null)
        : null;
    // The stored row a save updates; null means save creates a template.
    const editingTemplate = selectedTemplateId === null ? autoTemplate : selectedTemplate;
    // Countries and shortcode language of the template being edited.
    const [countries, setCountries] = useState([]);
    const [language, setLanguage] = useState(emailTemplateLanguages[0].locale);

    const [addEmailTemplate, { isLoading: addLoading }] = useAddEmailTemplateMutation();
    const [updateEmailTemplate, { isLoading: updateLoading }] = useUpdateEmailTemplateMutation();
    const [deleteEmailTemplate, { isLoading: deleteLoading }] = useDeleteEmailTemplateMutation();
    const isSaving = addLoading || updateLoading;

    const [subject, setSubject] = useState("");
    // viewMode: "editor" = editable rendered mail (designMode iframe), "html" = raw source.
    const [viewMode, setViewMode] = useState("editor");
    // The full email html document; the single source of truth for saving.
    const [htmlValue, setHtmlValue] = useState("");
    // Remount key for the editor iframe: srcDoc is only read on mount, so entering the
    // editor after source edits (or after re-hydration) forces a fresh document.
    const [iframeKey, setIframeKey] = useState(0);
    // Editing-canvas options: dark/light backdrop and frame size (null = fill).
    const [frameDark, setFrameDark] = useState(false);
    const [frameWidth, setFrameWidth] = useState(null);
    const [frameHeight, setFrameHeight] = useState(null);
    const iframeRef = useRef(null);
    const backdropRef = useRef(null);
    const hydratedIdRef = useRef("unhydrated");
    // Where the user last placed a caret: "subject" | "editor" | "html".
    const lastFocusRef = useRef(null);
    const subjectWrapRef = useRef(null);
    const htmlInputRef = useRef(null);
    // Position of the selection balloon inside the backdrop; null = hidden.
    const [balloon, setBalloon] = useState(null);

    const typeEntry = templateTypes?.find((entry) => entry.type === type);

    // What the editor shows: the open template; a new template starts from the scope's
    // all-countries template, else (for a saleschannel) the relation default, else the
    // stockhub default from /types — the fallback chain the mailer applies when sending.
    const startingTemplate = scopeRows.find(isForAllCountries) ?? (selectedChannelId ? defaultTemplate : null) ?? null;
    const effectiveTemplate = editingTemplate ?? startingTemplate;
    const templateSource = editingTemplate
        ? (selectedChannelId ? "specific" : "default")
        : (((selectedChannelId && defaultTemplate) || scopeRows.length > 0) ? "default" : "stockhub");

    // Both lists must be in (an error counts as "nothing stored"), so the screen always
    // shows what is actually being sent.
    const editorReady = typeEntry !== undefined
        && !scopeTemplatesFetching && (scopeTemplates !== undefined || scopeTemplatesError !== undefined)
        && !defaultScopeFetching && (defaultScopeTemplates !== undefined || defaultScopeError !== undefined);

    // Re-hydrate whenever the open template's identity changes (first load, scope
    // switch, template switch, new, delete/reset, environment switch) — never mid-fetch,
    // so typing survives background refetches.
    useEffect(() => {
        if (!editorReady) return;
        // A just-saved template is selected before the refetched list contains it; keep
        // the saved content on screen until the row arrives.
        if (typeof selectedTemplateId === "number" && !selectedTemplate) return;
        const hydrationKey = `${selectedChannelId ?? "default"}:${selectedTemplateId ?? "auto"}:${editingTemplate?.id ?? "-"}:${startingTemplate?.id ?? "-"}`;
        if (hydrationKey !== hydratedIdRef.current) {
            hydratedIdRef.current = hydrationKey;
            if (effectiveTemplate) {
                setSubject(effectiveTemplate.subject ?? "");
                const html = effectiveTemplate.bodyHtml || "";
                // Older templates stored a fragment; show it inside the shell the mailer adds.
                setHtmlValue(isFullHtmlDocument(html) ? html : wrapShellHtml(effectiveTemplate.subject ?? "", html));
                setLanguage(effectiveTemplate.language ?? emailTemplateLanguages[0].locale);
            } else {
                setSubject(typeEntry.defaultSubject ?? "");
                setHtmlValue(typeEntry.defaultBodyHtml ?? "");
                setLanguage(emailTemplateLanguages[0].locale);
            }
            // A new template starts without countries.
            setCountries(editingTemplate?.countries ?? []);
            setViewMode("editor");
            setIframeKey((key) => key + 1);
        }
    }, [selectedChannelId, selectedTemplateId, selectedTemplate, editingTemplate, startingTemplate, effectiveTemplate, editorReady, typeEntry]);

    // Read the (possibly edited) document out of the designMode iframe. Falls back to
    // htmlValue when the frame has no usable document (not loaded, emptied).
    const readEditorHtml = useCallback(() => {
        const doc = iframeRef.current?.contentDocument;
        if (doc && doc.body && doc.body.innerHTML.trim() !== "") {
            return "<!DOCTYPE html>" + doc.documentElement.outerHTML;
        }
        return htmlValue;
    }, [htmlValue]);

    const currentHtml = useCallback(
        () => (viewMode === "editor" ? readEditorHtml() : htmlValue),
        [viewMode, readEditorHtml, htmlValue]
    );

    const handleSizeChange = (setter) => (e) => {
        const value = parseInt(e.target.value, 10);
        setter(Number.isNaN(value) || value <= 0 ? null : value);
    };

    const switchToEditor = useCallback(() => {
        if (viewMode === "editor") return;
        setViewMode("editor");
        setIframeKey((key) => key + 1);
    }, [viewMode]);

    const switchToHtml = useCallback(() => {
        if (viewMode === "html") return;
        setHtmlValue(readEditorHtml());
        setViewMode("html");
        setBalloon(null);
    }, [viewMode, readEditorHtml]);

    // Position the selection balloon above the current (non-collapsed) selection.
    const updateBalloon = useCallback((doc) => {
        const selection = doc.getSelection ? doc.getSelection() : null;
        if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
            setBalloon(null);
            return;
        }
        const iframeEl = iframeRef.current;
        const backdropEl = backdropRef.current;
        if (!iframeEl || !backdropEl) {
            setBalloon(null);
            return;
        }
        const range = selection.getRangeAt(0);
        let rect = { top: 0, left: 0, width: 0 };
        try {
            if (typeof range.getBoundingClientRect === "function") {
                rect = range.getBoundingClientRect();
            }
        } catch (e) {
            // Selection rect unavailable — anchor the balloon to the frame's top.
        }
        const iframeRect = iframeEl.getBoundingClientRect();
        const backdropRect = backdropEl.getBoundingClientRect();
        const rawLeft = iframeRect.left - backdropRect.left + rect.left + rect.width / 2;
        setBalloon({
            top: Math.max(4, iframeRect.top - backdropRect.top + backdropEl.scrollTop + rect.top - 48),
            left: Math.min(Math.max(140, rawLeft), Math.max(140, backdropRect.width - 140)),
        });
    }, []);

    // Everything in the frame becomes editable; sandbox (same-origin, no scripts)
    // keeps pasted third-party templates from running code inside the app.
    const handleIframeLoad = useCallback((event) => {
        setBalloon(null);
        try {
            const doc = event.target.contentDocument;
            doc.designMode = "on";
            // Any caret movement inside the frame marks it as the last edit target
            // and drives the selection balloon.
            doc.addEventListener("selectionchange", () => {
                lastFocusRef.current = "editor";
                updateBalloon(doc);
            });
            // A stale balloon position is worse than no balloon while scrolling.
            doc.addEventListener("scroll", () => setBalloon(null), true);
            try {
                // Prefer inline css spans over <b>/<font> where the browser supports it.
                doc.execCommand("styleWithCSS", false, true);
            } catch (e) { /* optional nicety */ }
        } catch (e) {
            // Inaccessible document — the frame stays read-only.
        }
    }, [updateBalloon]);

    // After a balloon action, put the caret back in the frame so the user can keep
    // typing where the (still intact) selection is.
    const refocusEditor = () => {
        try {
            iframeRef.current?.contentWindow?.focus();
        } catch (e) { /* frame not focusable — nothing to restore */ }
    };

    // Formatting commands for the designMode document; selection stays intact because
    // the balloon lives in the parent document.
    const execEditorCommand = (command, value = null) => {
        const doc = iframeRef.current?.contentDocument;
        try {
            doc.execCommand(command, false, value);
        } catch (e) {
            // Command unsupported — leave the selection untouched.
        }
        refocusEditor();
    };

    // execCommand only knows sizes 1-7, so apply the classic trick: set the magic
    // size 7 with <font> output, then rewrite those elements to the exact pixel size.
    const applyFontSize = (px) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        try {
            doc.execCommand("styleWithCSS", false, false);
            doc.execCommand("fontSize", false, "7");
            doc.querySelectorAll('font[size="7"]').forEach((el) => {
                el.removeAttribute("size");
                el.style.fontSize = `${px}px`;
            });
            doc.execCommand("styleWithCSS", false, true);
        } catch (e) {
            // Command unsupported — leave the selection untouched.
        }
        refocusEditor();
    };

    // The shortcodes the backend allows for this template type; label keys must stay
    // literal t() calls below so i18n extraction picks them up.
    const shortcodes = typeEntry?.shortcodes ?? [];
    const shortcodeLabels = {
        customer_name: t("shortcode_customer_name"),
        company_name: t("shortcode_company_name"),
        street: t("shortcode_street"),
        house_nr: t("shortcode_house_nr"),
        house_nr_extra: t("shortcode_house_nr_extra"),
        postal: t("shortcode_postal"),
        city: t("shortcode_city"),
        country: t("shortcode_country"),
        shipper_name: t("shortcode_shipper_name"),
        tracking_number: t("shortcode_tracking_number"),
        tracking_link: t("shortcode_tracking_link"),
        tracking_button: t("shortcode_tracking_button"),
        relation_name: t("shortcode_relation_name"),
        webshopreference: t("shortcode_webshopreference"),
        shippingname: t("shortcode_shippingname"),
        content: t("shortcode_content"),
        supplier_name: t("shortcode_supplier_name"),
        reference: t("shortcode_reference"),
        shipment_date: t("shortcode_shipment_date"),
        remark: t("shortcode_remark"),
        products: t("shortcode_products"),
        pickup_address: t("shortcode_pickup_address"),
        warehouse_name: t("shortcode_warehouse_name"),
    };

    // Dropdown entries for the example shipment picker (client-side searchable).
    const shipmentOptions = (exampleShipments ?? []).map((shipment) => ({
        id: shipment.id,
        name: `${shipment.trackingNumber}${shipment.order?.webshopreference || shipment.order?.deliveryName
            ? ` - ${shipment.order?.webshopreference ?? shipment.order?.deliveryName}`
            : ""}`,
    }));

    // Clipboard fallback for when direct insertion is not possible.
    // Selector entries: the default template plus every saleschannel that sends
    // track & trace mails (the backend only returns enabled ones).
    const channelOptions = [
        { id: null, name: t("default_template") },
        ...(salesChannels ?? []).map((channel) => ({
            id: channel.id,
            name: `${channel.webshop_type} - ${channel.description ?? ""}`,
        })),
    ];

    // Every country is selectable for a template; the pool selector adds the region quick-selects.
    const allCountryCodes = countriesFromLocale("en").map((country) => country.alpha2);

    // The backend lists the languages it accepts; the names come from the frontend list.
    const languageName = (code) => emailTemplateLanguages.find((entry) => entry.locale === code)?.name ?? String(code ?? "").toUpperCase();
    const languageOptions = (typeEntry?.languages ?? emailTemplateLanguages.map((entry) => entry.locale)).map((code) => ({
        id: code,
        name: languageName(code),
    }));

    // Selector entries: every stored template of the scope, named by its countries and language.
    const templateOptions = scopeRows.map((row) => ({
        id: row.id,
        name: `${isForAllCountries(row) ? t("all_countries") : row.countries.map((code) => String(code).toUpperCase()).join(", ")} - ${languageName(row.language ?? emailTemplateLanguages[0].locale)}`,
    }));

    const copyShortcode = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showToastMessage("success", t("shortcode_copied"));
        } catch (err) {
            showToastMessage("error", t("shortcode_copy_failed"));
        }
    };

    // Splice text into a value at the input's caret (end when the caret is unknown),
    // and put the caret right after the insertion once the re-render is done.
    const insertAtInputCaret = (inputEl, value, text, apply) => {
        const start = inputEl?.selectionStart ?? value.length;
        const end = inputEl?.selectionEnd ?? start;
        apply(value.slice(0, start) + text + value.slice(end));
        if (inputEl) {
            setTimeout(() => {
                inputEl.focus();
                inputEl.setSelectionRange(start + text.length, start + text.length);
            }, 0);
        }
    };

    // Insert the shortcode where the user was last editing: the subject field, the
    // designMode frame (at its caret) or the html source (at its caret).
    const insertShortcode = (code) => {
        const text = `{{${code}}}`;

        if (lastFocusRef.current === "subject") {
            const inputEl = subjectWrapRef.current?.querySelector("input");
            insertAtInputCaret(inputEl, subject, text, setSubject);
            return;
        }

        if (viewMode === "editor") {
            const doc = iframeRef.current?.contentDocument;
            try {
                // Without a caret in the frame yet, insert at the end of the mail
                // instead of the browser's default position (the very start).
                if (lastFocusRef.current !== "editor") {
                    const range = doc.createRange();
                    range.selectNodeContents(doc.body);
                    range.collapse(false);
                    const selection = doc.getSelection();
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
                if (!doc.execCommand("insertText", false, text)) {
                    throw new Error("insertText rejected");
                }
            } catch (e) {
                copyShortcode(text);
            }
            return;
        }

        insertAtInputCaret(htmlInputRef.current, htmlValue, text, setHtmlValue);
    };

    // Which confirmation modal is open: "delete" (channel template), "reset" (default
    // template back to stockhub) or "applyAll" (save content as the default template).
    const [confirmAction, setConfirmAction] = useState(null);

    // Some types carry mandatory shortcodes (the backend enforces this too); saving
    // without them would silently drop the mail's dynamic content.
    const missingRequiredShortcode = useCallback((html) => {
        const required = typeEntry?.requiredShortcodes ?? [];
        return required.find((code) => !String(html ?? "").includes(`{{${code}}}`)) || null;
    }, [typeEntry]);

    // The full html document is authoritative; body (tiptap json) is not used by this editor.
    // Saves to the selected scope: the saleschannel's own template, or the default one.
    const saveTemplate = useCallback(async () => {
        if (isSaving) return;
        const bodyHtml = currentHtml();
        const missing = missingRequiredShortcode(bodyHtml);
        if (missing) {
            showToastMessage("error", `${t("shortcode_required_error")} {{${missing}}}`);
            return;
        }
        // A second template without countries would collide with the scope's all-countries template.
        const clashesWithAllCountries = countries.length === 0
            && scopeRows.some((row) => isForAllCountries(row) && row.id !== editingTemplate?.id);
        if (clashesWithAllCountries) {
            showToastMessage("error", t("all_countries_template_exists"));
            return;
        }
        const payload = { subject, body: null, bodyHtml, countries, language };

        // A failed mutation carries error (not data) — e.g. server-side validation.
        // On success htmlValue is synced with what was saved: the invalidation refetch
        // briefly unmounts the editor iframe, and its remount reads htmlValue. The saved
        // row stays open.
        const selectSaved = (saved) => { if (saved?.id) setSelectedTemplateId(saved.id); };
        if (editingTemplate?.id) {
            const response = await updateEmailTemplate({ id: editingTemplate.id, ...payload });
            if (response.error) showToastMessageRequestError(t("update_failed"), t, response.error);
            else {
                setHtmlValue(bodyHtml);
                selectSaved(response.data);
                showToastMessageUpdateRequest(t, response.data);
            }
        } else {
            const response = await addEmailTemplate({ type, webshopCredentialsId: selectedChannelId, ...payload });
            if (response.error) showToastMessageRequestError(t("create_failed"), t, response.error);
            else {
                setHtmlValue(bodyHtml);
                selectSaved(response.data);
                showToastMessagePostRequest(t, response.data);
            }
        }
    }, [isSaving, subject, currentHtml, missingRequiredShortcode, editingTemplate, scopeRows, countries, language, selectedChannelId, type, updateEmailTemplate, addEmailTemplate, t]);

    // Save the current content as the DEFAULT template — one mail for every channel
    // without its own template — instead of creating one per saleschannel. Countries are
    // left out on purpose: the default keeps (or starts with) its own country list.
    const applyToAll = useCallback(async () => {
        if (isSaving) return;
        const bodyHtml = currentHtml();
        const payload = { subject, body: null, bodyHtml, language };

        if (defaultTemplate?.id) {
            const response = await updateEmailTemplate({ id: defaultTemplate.id, ...payload });
            if (response.error) showToastMessageRequestError(t("update_failed"), t, response.error);
            else {
                setHtmlValue(bodyHtml);
                showToastMessageUpdateRequest(t, response.data);
            }
        } else {
            const response = await addEmailTemplate({ type, webshopCredentialsId: null, ...payload });
            if (response.error) showToastMessageRequestError(t("create_failed"), t, response.error);
            else {
                setHtmlValue(bodyHtml);
                showToastMessagePostRequest(t, response.data);
            }
        }
    }, [isSaving, subject, currentHtml, language, defaultTemplate, type, updateEmailTemplate, addEmailTemplate, t]);

    // Deletes the selected scope's template: a channel falls back to the default
    // template, the default falls back to the stockhub mail. Local state is not wiped
    // here: the delete invalidates the queries and the re-hydration effect above
    // resets subject/html once the fallback comes back.
    const deleteCurrentTemplate = useCallback(async () => {
        if (!editingTemplate?.id || deleteLoading) return;
        const response = await deleteEmailTemplate(editingTemplate.id);
        if (response.error) showToastMessageRequestError(t("delete_failed"), t, response.error);
        else {
            // After a delete the selector falls back to the scope's remaining template.
            setSelectedTemplateId(null);
            showToastMessageDeleteRequest(t, response.data);
        }
    }, [editingTemplate, deleteLoading, deleteEmailTemplate, t]);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions hasManagePermission={hasManagePermission} action={saveTemplate} isLoading={isSaving} />
        });

        return () => {};
    }, [hasManagePermission, saveTemplate, isSaving]);

    return (
        <Box
            sx={{
                pt: 4,
                height: 1,
                display: "flex",
                flexDirection: "column",
                "@media screen and (max-width: 47.9375em)": {
                    paddingTop: 0,
                },
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    paddingX: 4,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingX: 2,
                    },
                }}
            >
                <Box
                    sx={{
                        "@media screen and (max-width: 47.9375em)": {
                            display: "none",
                        },
                    }}
                >
                    <Subheading>{t(titleKey)}</Subheading>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "grid",
                        gridTemplateColumns: "1fr minmax(260px, 320px)",
                        gap: 4,
                        "@media screen and (max-width: 1023px)": {
                            gridTemplateColumns: "1fr",
                        },
                    }}
                >
                    {/* Email */}
                    <Box
                        sx={{
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                            pb: 4,
                        }}
                    >
                        {enableChannels &&
                            <Box>
                                <Text bold>{t("saleschannel")}</Text>
                                <Box sx={{ mt: 0.5 }}>
                                    <Text light>
                                        {templateSource === "specific" && t("template_source_specific")}
                                        {templateSource === "default" && (selectedChannelId ? t("template_source_default") : "")}
                                        {templateSource === "stockhub" && t("template_source_stockhub")}
                                    </Text>
                                </Box>
                                <Box sx={{ mt: 1, maxWidth: 480 }}>
                                    <InputDropdown
                                        displayName="name"
                                        selected={channelOptions.findIndex((option) => option.id === selectedChannelId)}
                                        options={channelOptions}
                                        noSelection={t("default_template")}
                                        onChange={(index) => {
                                            // Templates belong to a scope, so a scope switch opens that scope's own template.
                                            setSelectedChannelId(channelOptions[index]?.id ?? null);
                                            setSelectedTemplateId(null);
                                        }}
                                        closeOnSelection
                                    />
                                </Box>
                            </Box>
                        }

                        <Box>
                            <Text bold>{t("email_template")}</Text>
                            <Box sx={{ mt: 0.5 }}>
                                <Text light>{t("template_selector_explanation")}</Text>
                            </Box>
                            <Box sx={{ mt: 1, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                <Box sx={{ flex: "1 1 240px", maxWidth: 480 }}>
                                    <InputDropdown
                                        displayName="name"
                                        selected={templateOptions.findIndex((option) => option.id === editingTemplate?.id)}
                                        options={templateOptions}
                                        noSelection={t("unsaved_template")}
                                        onChange={(index) => setSelectedTemplateId(templateOptions[index]?.id ?? null)}
                                        closeOnSelection
                                    />
                                </Box>
                                {hasManagePermission &&
                                    <Shbutton
                                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                        variant="contained"
                                        onClick={() => setSelectedTemplateId("new")}
                                        endIcon={<AddIcon />}
                                    >
                                        {t("new_template")}
                                    </Shbutton>
                                }
                            </Box>
                        </Box>

                        <Box>
                            <Text bold>{t("countries")}</Text>
                            <Box sx={{ mt: 0.5 }}>
                                <Text light>{t("template_countries_explanation")}</Text>
                            </Box>
                            {hasManagePermission &&
                                <Box sx={{ mt: 1, maxWidth: 480 }}>
                                    <CountryPoolSelector
                                        countryPool={allCountryCodes}
                                        selected={countries}
                                        onChange={setCountries}
                                    />
                                </Box>
                            }
                        </Box>

                        <Box>
                            <Text bold>{t("language")}</Text>
                            <Box sx={{ mt: 0.5 }}>
                                <Text light>{t("template_language_explanation")}</Text>
                            </Box>
                            <Box sx={{ mt: 1, maxWidth: 480 }}>
                                <InputDropdown
                                    displayName="name"
                                    selected={languageOptions.findIndex((option) => option.id === language)}
                                    options={languageOptions}
                                    noSelection={t("language")}
                                    onChange={(index) => setLanguage(languageOptions[index]?.id ?? emailTemplateLanguages[0].locale)}
                                    closeOnSelection
                                />
                            </Box>
                        </Box>

                        {showSubject &&
                            <Box>
                                <Text bold>{t("email_subject")}</Text>
                                <Box
                                    ref={subjectWrapRef}
                                    onFocus={() => { lastFocusRef.current = "subject"; }}
                                    sx={{ mt: 1, maxWidth: 480 }}
                                >
                                    <TextInput
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        sx={{ width: 1 }}
                                    />
                                </Box>
                            </Box>
                        }

                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Text bold>{t("content")}</Text>
                            <Box
                                sx={{
                                    mt: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 2,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                                        borderRadius: 3,
                                        overflow: "hidden",
                                    }}
                                >
                                    {[
                                        { key: "editor", label: t("editor"), onClick: switchToEditor },
                                        { key: "html", label: "HTML", onClick: switchToHtml },
                                    ].map((mode) => (
                                        <ButtonBase
                                            key={mode.key}
                                            data-testid={`view-${mode.key}`}
                                            onClick={mode.onClick}
                                            sx={{
                                                px: 1.5,
                                                height: 34,
                                                fontSize: 13,
                                                fontWeight: 500,
                                                fontFamily: "Roboto, sans-serif",
                                                background: viewMode === mode.key
                                                    ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                                                    : "transparent",
                                                color: viewMode === mode.key
                                                    ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                                    : colors.txt["secondary"],
                                            }}
                                        >
                                            {mode.label}
                                        </ButtonBase>
                                    ))}
                                </Box>
                                {viewMode === "editor" && <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                                            borderRadius: 3,
                                            overflow: "hidden",
                                        }}
                                    >
                                        {[
                                            { key: "light", dark: false, icon: <LightModeOutlinedIcon sx={{ fontSize: 18 }} /> },
                                            { key: "dark", dark: true, icon: <DarkModeOutlinedIcon sx={{ fontSize: 18 }} /> },
                                        ].map((option) => (
                                            <IconButton
                                                key={option.key}
                                                size="small"
                                                data-testid={`frame-theme-${option.key}`}
                                                onClick={() => setFrameDark(option.dark)}
                                                sx={{
                                                    borderRadius: 0,
                                                    px: 1.5,
                                                    background: frameDark === option.dark
                                                        ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                                                        : "transparent",
                                                    color: frameDark === option.dark
                                                        ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                                        : colors.txt["secondary"],
                                                }}
                                            >
                                                {option.icon}
                                            </IconButton>
                                        ))}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                                            borderRadius: 3,
                                            overflow: "hidden",
                                        }}
                                    >
                                        {FRAME_DEVICES.map((device) => (
                                            <IconButton
                                                key={device.key}
                                                size="small"
                                                data-testid={`frame-device-${device.key}`}
                                                onClick={() => setFrameWidth(device.width)}
                                                sx={{
                                                    borderRadius: 0,
                                                    px: 1.5,
                                                    background: frameWidth === device.width
                                                        ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                                                        : "transparent",
                                                    color: frameWidth === device.width
                                                        ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                                        : colors.txt["secondary"],
                                                }}
                                            >
                                                {device.icon}
                                            </IconButton>
                                        ))}
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <TextInput
                                            type="number"
                                            value={frameWidth ?? ""}
                                            placeholder={"auto"}
                                            onChange={handleSizeChange(setFrameWidth)}
                                            sx={{ width: 84 }}
                                            className={"frame-width"}
                                        />
                                        <Text light>×</Text>
                                        <TextInput
                                            type="number"
                                            value={frameHeight ?? ""}
                                            placeholder={"auto"}
                                            onChange={handleSizeChange(setFrameHeight)}
                                            sx={{ width: 84 }}
                                            className={"frame-height"}
                                        />
                                    </Box>
                                </>}
                            </Box>
                            <Box
                                sx={{
                                    mt: 1,
                                    flex: 1,
                                    minHeight: "max(360px, calc(100vh - 560px))",
                                    display: "flex",
                                    flexDirection: "column",
                                    background: viewMode === "editor"
                                        ? "transparent"
                                        : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]),
                                    borderRadius: 4,
                                    padding: viewMode === "editor" ? 0 : 2,
                                    overflow: "hidden",
                                }}
                            >
                                {editorReady && viewMode === "editor" &&
                                    <Box
                                        ref={backdropRef}
                                        data-testid="editor-backdrop"
                                        style={{ background: frameDark ? "#1b1c22" : "#f4f4f4" }}
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            display: "flex",
                                            justifyContent: "center",
                                            overflow: "auto",
                                            position: "relative",
                                        }}
                                    >
                                        <Box
                                            component="iframe"
                                            key={iframeKey}
                                            ref={iframeRef}
                                            title={t("editor")}
                                            sandbox="allow-same-origin"
                                            srcDoc={htmlValue}
                                            onLoad={handleIframeLoad}
                                            data-testid="editor-frame"
                                            style={{
                                                width: frameWidth ? `${frameWidth}px` : "100%",
                                                height: frameHeight ? `${frameHeight}px` : "100%",
                                            }}
                                            sx={{
                                                border: "none",
                                                flexShrink: 0,
                                                maxWidth: 1,
                                                background: "#f4f4f4",
                                                boxShadow: frameWidth ? "0 0 0 1px rgba(0,0,0,0.08)" : "none",
                                            }}
                                        />
                                        {balloon &&
                                            <Box
                                                data-testid="editor-balloon"
                                                onMouseDown={(e) => {
                                                    // Native selects only open on their default mousedown;
                                                    // everything else is prevented so the iframe selection
                                                    // survives clicking the balloon.
                                                    if (!e.target.closest("select")) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                style={{ top: balloon.top, left: balloon.left }}
                                                sx={{
                                                    position: "absolute",
                                                    zIndex: 5,
                                                    transform: "translateX(-50%)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                                                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    data-testid="balloon-bold"
                                                    onClick={() => execEditorCommand("bold")}
                                                    sx={{ color: colors.txt["primary"] }}
                                                >
                                                    <FormatBoldIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    data-testid="balloon-italic"
                                                    onClick={() => execEditorCommand("italic")}
                                                    sx={{ color: colors.txt["primary"] }}
                                                >
                                                    <FormatItalicIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                                <Box
                                                    component="select"
                                                    defaultValue=""
                                                    aria-label={t("font")}
                                                    data-testid="balloon-font"
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            execEditorCommand("fontName", e.target.value);
                                                            e.target.value = "";
                                                        }
                                                    }}
                                                    sx={{
                                                        border: "none",
                                                        outline: "none",
                                                        background: "transparent",
                                                        color: colors.txt["primary"],
                                                        fontSize: 13,
                                                        maxWidth: 110,
                                                    }}
                                                >
                                                    <option value="" disabled>{t("font")}</option>
                                                    {BALLOON_FONTS.map((font) => (
                                                        <option key={font} value={font}>{font}</option>
                                                    ))}
                                                </Box>
                                                <Box
                                                    component="select"
                                                    defaultValue=""
                                                    aria-label={t("font_size")}
                                                    data-testid="balloon-font-size"
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            applyFontSize(parseInt(e.target.value, 10));
                                                            e.target.value = "";
                                                        }
                                                    }}
                                                    sx={{
                                                        border: "none",
                                                        outline: "none",
                                                        background: "transparent",
                                                        color: colors.txt["primary"],
                                                        fontSize: 13,
                                                        maxWidth: 80,
                                                    }}
                                                >
                                                    <option value="" disabled>{t("font_size")}</option>
                                                    {BALLOON_FONT_SIZES.map((size) => (
                                                        <option key={size} value={size}>{size}px</option>
                                                    ))}
                                                </Box>
                                            </Box>
                                        }
                                    </Box>
                                }
                                {editorReady && viewMode === "html" &&
                                    <InputBase
                                        multiline
                                        fullWidth
                                        value={htmlValue}
                                        onChange={(e) => setHtmlValue(e.target.value)}
                                        onFocus={() => { lastFocusRef.current = "html"; }}
                                        inputRef={htmlInputRef}
                                        placeholder={t("email_template_placeholder")}
                                        inputProps={{ "data-testid": "html-input" }}
                                        sx={{
                                            flex: 1,
                                            alignItems: "flex-start",
                                            fontFamily: "monospace",
                                            fontSize: 13,
                                            color: colors.txt["primary"],
                                            "& textarea": {
                                                height: "100% !important",
                                                overflow: "auto !important",
                                            },
                                        }}
                                    />
                                }
                            </Box>
                        </Box>

                        {hasManagePermission &&
                            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
                                {editingTemplate?.id && (selectedChannelId || !isForAllCountries(editingTemplate)) &&
                                    <Shbutton
                                        isLoading={deleteLoading}
                                        color={"red"}
                                        variant="outlined"
                                        onClick={() => setConfirmAction("delete")}
                                        endIcon={<RestartAltIcon />}
                                    >
                                        {t("delete")}
                                    </Shbutton>
                                }
                                {editingTemplate?.id && !selectedChannelId && isForAllCountries(editingTemplate) &&
                                    <Shbutton
                                        isLoading={deleteLoading}
                                        color={"red"}
                                        variant="outlined"
                                        onClick={() => setConfirmAction("reset")}
                                        endIcon={<RestartAltIcon />}
                                    >
                                        {t("reset_to_default")}
                                    </Shbutton>
                                }
                                {selectedChannelId &&
                                    <Shbutton
                                        isLoading={isSaving}
                                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                        variant="outlined"
                                        onClick={() => setConfirmAction("applyAll")}
                                    >
                                        {t("apply_to_all")}
                                    </Shbutton>
                                }
                                <Shbutton
                                    isLoading={isSaving}
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    variant="contained"
                                    onClick={saveTemplate}
                                    endIcon={<SaveOutlinedIcon />}
                                >
                                    {t("save")}
                                </Shbutton>
                            </Box>
                        }
                    </Box>

                    {/* Shortcodes */}
                    <Box
                        sx={{
                            borderLeft: `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                            paddingLeft: 4,
                            pb: 4,
                            "@media screen and (max-width: 1023px)": {
                                borderLeft: "none",
                                paddingLeft: 0,
                                borderTop: `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                paddingTop: 3,
                            },
                        }}
                    >
                        <Text light>{t("email_shortcodes_explanation")}</Text>

                        {enableShipmentPreview &&
                            <Box sx={{ mt: 2 }}>
                                <Text light>{t("example_shipment")}</Text>
                                <Box sx={{ mt: 1 }}>
                                    <InputDropdown
                                        displayName="name"
                                        selected={shipmentOptions.findIndex((option) => option.id === exampleShipmentId)}
                                        options={shipmentOptions}
                                        noSelection={t("choose_shipment")}
                                        onChange={(index) => setExampleShipmentId(shipmentOptions[index]?.id ?? null)}
                                        closeOnSelection
                                        searchOptions={{
                                            enabled: true,
                                            placeholder: t("search"),
                                            onSearch: setShipmentSearch,
                                        }}
                                    />
                                </Box>
                            </Box>
                        }

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                flexDirection: shipmentPreview && exampleShipmentId ? "column" : "row",
                                alignItems: "flex-start",
                                gap: 1,
                                mt: 1.5,
                            }}
                        >
                            {shortcodes.map((code) => (
                                <Box key={code} sx={{ maxWidth: 1 }}>
                                    <Chip
                                        label={`${shortcodeLabels[code] ?? code} · {{${code}}}`}
                                        onClick={() => insertShortcode(code)}
                                        size="small"
                                        sx={{
                                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                            color: colors.txt["primary"],
                                        }}
                                    />
                                    {shipmentPreview && exampleShipmentId &&
                                        <Box
                                            sx={{
                                                pl: 1.5,
                                                pb: 0.5,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <Text light>{String(shipmentPreview[code] ?? "-")}</Text>
                                        </Box>
                                    }
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <MConfirmDelete
                open={confirmAction === "delete"}
                handleClose={() => setConfirmAction(null)}
                title={t("delete_channel_template_title")}
                content={t("delete_channel_template_subtitle")}
                confirm={deleteCurrentTemplate}
            />
            <MConfirmDelete
                open={confirmAction === "reset"}
                handleClose={() => setConfirmAction(null)}
                title={t("reset_default_template_title")}
                content={t("reset_default_template_subtitle")}
                confirmBtn={t("reset_to_default")}
                confirm={deleteCurrentTemplate}
            />
            <MConfirmation
                open={confirmAction === "applyAll"}
                handleClose={() => setConfirmAction(null)}
                title={t("apply_to_all")}
                subtitle={t("apply_to_all_confirm_subtitle")}
                confirm={applyToAll}
            />
        </Box>
    );
};

export default EmailTemplateEditor;
